import { useUploadStore } from "@components/FileUpload/uploadStore";
import { queryClient } from "@config/queryClient";
import { useNotification } from "@context/NotificationContext";
import { mapVesselCallStoreToCreateRequest } from "@features/vessel-call/create/mappers/mapVesselCallStoreToCreateRequest";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import {
  DRAFT_STATUSES,
  UPDATEBLE_VESSEL_CALL_STATUSES,
} from "@features/vessel-call/vessel-call-status/flow/utils";
import { hasAlreadyActiveVesselCall } from "@features/vessel-call/vessel-scan/utils";
import type { Vessel } from "@services/vessel/types";
import type { VesselCallCreateRequest } from "@services/vesselCall/types";
import {
  createDraftVesselCall,
  createNewDraftVesselCall,
  createVesselCall,
  resubmitVesselCall,
  updateDraftVesselCall,
} from "@services/vesselCallApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { paths } from "@utils/navigation";
import { cleanVesselCallPayload } from "@utils/payloadClean";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  validateCertificatesWillExpireSoon,
  validateVesselCertificates,
} from "../utils/validateCertificates";

type UseCreateVesselCallProps = {
  resetForm?: () => void;
  existingCallId?: string | null;
  existingCallStatus?: string | null;
  vesselDetails?: Vessel | null;
};

export const useCreateVesselCall = ({
  resetForm,
  existingCallId,
  existingCallStatus,
  vesselDetails,
}: UseCreateVesselCallProps = {}) => {
  const isPreArrivalDraft = existingCallStatus === "PRE_ARRIVAL_DRAFT";
  const isRejected = UPDATEBLE_VESSEL_CALL_STATUSES.includes(existingCallStatus ?? "");
  const navigate = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const { t } = useTranslation();
  const { notify } = useNotification();
  const clearUploadStore = useUploadStore((state) => state.clearAll);
  const locationState = location.state as {
    prefillFromId?: string;
    originalVesselCallId?: string;
    finalSubmission?: boolean;
  } | null;
  const prefillFromId = locationState?.prefillFromId ?? search.get("prefillFromId");
  const originalVesselCallId =
    locationState?.originalVesselCallId ?? search.get("originalVesselCallId");
  const submitTargetId = existingCallId;
  const isExistingDraft = DRAFT_STATUSES.includes(existingCallStatus ?? "");
  const isNewDraftFlow = !prefillFromId && !existingCallId;

  const isFinalSubmission =
    locationState?.finalSubmission === true || search.get("finalSubmission") === "true";

  const shouldResubmit =
    !!submitTargetId &&
    (isRejected || (isFinalSubmission && (!isExistingDraft || !!originalVesselCallId)));

  const mutation = useMutation({
    mutationFn: ({ payload, isDraft }: { payload: VesselCallCreateRequest; isDraft?: boolean }) => {
      const cleanPayload = cleanVesselCallPayload(payload);
      const createPayload =
        isPreArrivalDraft && existingCallId
          ? {
              ...cleanPayload,
              id: existingCallId,
            }
          : cleanPayload;

      return runTransaction(
        {
          module: "vessel_call",
          screen: "CreateVesselCall",
          action: isDraft
            ? "save_vessel_call_draft"
            : shouldResubmit
              ? "resubmit_vessel_call"
              : "submit_vessel_call",
          entityType: "vessel_call",
        },
        (transactionId) => {
          if (isDraft) {
            if (originalVesselCallId && existingCallId) {
              return updateDraftVesselCall(
                existingCallId,
                cleanPayload,
                ...(transactionId ? [transactionId] : []),
              );
            }

            if (existingCallId) {
              return createDraftVesselCall(
                existingCallId,
                cleanPayload,
                ...(transactionId ? [transactionId] : []),
              );
            }

            if (isNewDraftFlow) {
              return createNewDraftVesselCall(
                cleanPayload,
                ...(transactionId ? [transactionId] : []),
              );
            }

            throw new Error("Unable to determine draft vessel call save flow");
          }

          if (shouldResubmit) {
            return resubmitVesselCall(
              submitTargetId,
              cleanPayload,
              ...(transactionId ? [transactionId] : []),
            );
          }

          return createVesselCall(createPayload, ...(transactionId ? [transactionId] : []));
        },
      );
    },
    onSuccess: async (_data, variables) => {
      clearUploadStore();

      const state = useVesselCallFormStore.getState();
      const expiringSoonCertificates = validateCertificatesWillExpireSoon(
        state.expectedArrivalLocation,
        vesselDetails,
      );

      notify({
        title: variables?.isDraft
          ? t("vessel-call.create.notifications.vessel-call-draft-saved-success-title")
          : t("vessel-call.create.notifications.vessel-call-submitted-success-title"),
        description: variables?.isDraft
          ? t("vessel-call.create.notifications.vessel-call-draft-saved-success-description")
          : t("vessel-call.create.notifications.vessel-call-submitted-success-description"),
        type: "success",
      });

      if (expiringSoonCertificates.length > 0 && isFinalSubmission && !variables?.isDraft) {
        notify({
          title: t("vessel-call.create.notifications.certificate-will-expire-title"),
          description: t("vessel-call.create.notifications.certificates-expire-soon"),
          type: "warning",
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["vesselCall"] }),
        queryClient.invalidateQueries({ queryKey: ["vesselCall.prefill"] }),
      ]);

      resetForm?.();
      navigate(`/${paths.vesselCall.index}`);
    },
    onError: (err: any, variables: any) => {
      if (hasAlreadyActiveVesselCall(err)) {
        notify({
          title: t("vessel-call.vessel-scan.already-active-error-title"),
          description: t("vessel-call.vessel-scan.already-active-error-description"),
          type: "error",
        });
        return;
      }
      notify({
        title: variables?.isDraft
          ? t("vessel-call.create.notifications.vessel-call-draft-saved-error-title")
          : t("vessel-call.create.notifications.vessel-call-submitted-error-title"),
        description:
          err?.response?.data?.detail ??
          err?.message ??
          (variables?.isDraft
            ? t("vessel-call.create.notifications.vessel-call-draft-saved-error-description")
            : t("vessel-call.create.notifications.vessel-call-submitted-error-description")),
        type: "error",
      });
    },
  });

  const onSubmit = async (options?: { isDraft?: boolean }) => {
    const state = useVesselCallFormStore.getState();
    const isDraft = options?.isDraft === true;

    if (isFinalSubmission && !isDraft) {
      const invalidCertificates = validateVesselCertificates(
        state.expectedArrivalLocation,
        vesselDetails,
        vesselDetails?.diplomaticVessel,
      );

      if (invalidCertificates.length > 0) {
        const certList = invalidCertificates
          .map((cert) => `- ${t(`vessel-management.certificates.${cert}`)}`)
          .join("\n");

        notify({
          title: t("vessel-call.create.notifications.certificate-validation-title"),
          description: `${t("vessel-call.create.notifications.certificate-validation-description")}\n \n${certList}`,
          type: "error",
          autoHideDuration: 60 * 1000,
        });
        return; //submission blocked due to expired/missing certificates
      }
    }
    const payload = mapVesselCallStoreToCreateRequest(state);
    mutation.mutate({ payload, isDraft });
  };

  return { onSubmit };
};
